import { useApolloClient, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  Paper,
  Popover,
  Typography,
} from '@mui/material';
import type { MutableRefObject } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ButtonLink, Link } from '~/components/link';
import { useAppContext } from '~/contexts';
import { LogoutDocument } from '~/generated/graphql';
import { useLink } from '~/hooks/link';
import { resetWebsocketConnection } from '~/lib/apollo';
import { supportEmail } from '~/lib/constants';
import { NetworkAvatar } from './NetworkAvatar';

export interface UserActionsPopoverProps {
  anchorEl: MutableRefObject<HTMLButtonElement | null>;
  isOpen: boolean;
  onCloseMenu: (event: React.MouseEvent<HTMLElement | Document> | MouseEvent | TouchEvent) => void;
  onEscape: () => void;
}

export const UserActionsPopover = ({
  anchorEl,
  isOpen,
  onCloseMenu,
  onEscape,
}: UserActionsPopoverProps) => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();

  const link = useLink();

  const client = useApolloClient();

  const navigate = useNavigate();

  const [logout] = useMutation(LogoutDocument, {
    async onCompleted() {
      resetWebsocketConnection();
      await client.resetStore();
    },
  });

  return (
    <Popover
      open={isOpen}
      // Clicks outside are left to the ClickAwayListener below.
      onClose={(_, reason) => {
        if (reason === 'escapeKeyDown') onEscape();
      }}
      anchorEl={anchorEl.current}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      disablePortal
      slotProps={{
        paper: {
          sx: {
            width: '300px',
            border: 1,
            borderColor: 'borders.main',
            padding: (theme) => theme.spacing(2),
          },
        },
      }}
      TransitionComponent={Collapse}
    >
      <Paper sx={{ boxShadow: 'none' }}>
        <ClickAwayListener onClickAway={onCloseMenu}>
          <Box
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'center'}
            alignItems={'center'}
            width={'100%'}
          >
            <NetworkAvatar icon={currentNetwork.logoUrl} sx={{ width: 56, height: 56 }} />
            <Typography sx={{ mt: 1 }} variant="h6">
              {currentUser.name}
            </Typography>
            <Typography sx={{ fontSize: '.85em' }}>{currentUser.email}</Typography>
            <ButtonLink
              type="submit"
              variant="contained"
              fullWidth
              color="primary"
              onClick={onCloseMenu}
              size="medium"
              sx={{ mt: 1 }}
              to={link('/profile')}
            >
              {t('common.user_actions.manage_account')}
            </ButtonLink>
            <Typography align="center">
              <Button
                onClick={(event) => {
                  onCloseMenu(event);
                  void logout().then(() => navigate('/login'));
                }}
                sx={{
                  color: 'common.black',
                  mt: 0.5,
                  '&:hover': { backgroundColor: 'transparent !important' },
                }}
              >
                {t('common.user_actions.sign_out')}
              </Button>
            </Typography>
            <Box
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
              alignItems={'center'}
              width={'100%'}
              borderTop={1}
              borderColor="borders.light"
            >
              <Link
                onClick={onCloseMenu}
                sx={{
                  color: 'common.black',
                  fontSize: '.85em',
                  mt: 1,
                  textDecoration: 'none',
                }}
                to={`mailto:${supportEmail}`}
              >
                {t('common.user_actions.contact_support')}
              </Link>
            </Box>
          </Box>
        </ClickAwayListener>
      </Paper>
    </Popover>
  );
};
